/**
 * * @typedef {Object} BundleComponent - Component for the product bundle
 * @property {string} title
 * @property {number} variant_id
 * @property {string} sku
 * @property {boolean} available
 * @property {number} inventory_quantity
 * @property {string[]} mapping - ["Irons", "Wedges"]
 * @property {string} default_weight_configuration - Putters only
 * @property {string} type - 'head', 'shaft', 'grip', 'headcover', or 'soft_goods'
 * @property {string} dexterity - L or R
 */

if (!customElements.get('clubset-selects')) {
  customElements.define(
    'clubset-selects',
    class ClubsetSelects extends HTMLElement {
      constructor() {
        super();

        this.dexterity = 'R';
        this.shaftFlex = 'regular';
        this.clubsetData = null;
        this.clubCount = 9;
        this.bundleId = Date.now();
      }

      connectedCallback() {
        this.clubsetData = this.getClubsetData();
        if (!this.clubsetData) return;
        this.setInitialValues();
        this.buildBundle();
        this.addEventListener('change', this.onChange.bind(this));
      }

      /**
       * Handle changes to the form
       * @param {Event} event - The change event
       */
      onChange(event) {
        if (event.target.name === 'properties[Dexterity]') {
          this.dexterity = event.target.dataset.clubsetValue;
        } else if (event.target.name === 'properties[Shaft Flex]') {
          this.shaftFlex = event.target.dataset.clubsetValue;
        }

        this.buildBundle();
      }

      /**
       * Check if mapping1 has any items in mapping2
       * @param {string[]} mapping1 - The first mapping
       * @param {string[]} mapping2 - The second mapping
       * @returns {boolean} - True if mapping1 has any items in mapping2, false otherwise
       */
      hasMappingMatch(mapping1, mapping2) {
        return mapping1.some((item) => mapping2.includes(item));
      }

      /**
       * Generate a UUID
       * @param {number} variantId - The variant ID
       * @returns {string} - The UUID
       */
      generateUuid(variantId) {
        return `${variantId}-${Math.random().toString(36).substring(2, 15)}`;
      }

      /**
       * Get the clubsetData JSONfrom the DOM
       * @returns {Object} - The clubsetData object
       */
      getClubsetData() {
        const clubsetData = this.querySelector('[data-clubset]')?.innerHTML;
        try {
          return JSON.parse(clubsetData);
        } catch (error) {
          this.setBundleSoldOut(true, window.variantStrings.soldOut);
          console.error('CLUBSET DATA ERROR', error);
          return null;
        }
      }

      /**
       * Set the initial values from the selected dexterity and shaft flex
       */
      setInitialValues() {
        const selectedDexterity = this.querySelector('input[name="properties[Dexterity]"]:checked');
        this.dexterity = selectedDexterity ? selectedDexterity.dataset.clubsetValue : 'R';
        const selectedShaftFlex = this.querySelector('input[name="properties[Shaft Flex]"]:checked');
        this.shaftFlex = selectedShaftFlex ? selectedShaftFlex.dataset.clubsetValue : 'regular';
      }

      /**
       * Set the bundle sold out state
       * @param {boolean} isSoldOut - True if the bundle is sold out, false otherwise
       */
      setBundleSoldOut(isSoldOut, text) {
        const productForm = document.querySelector('product-form');
        if (productForm) {
          productForm.toggleSubmitButton(isSoldOut, text);
        }
      }

      /**
       * Set the club count
       * @param {number} count - The number of clubs in the bundle
       */
      setClubCount(count) {
        this.clubCount = count;
        this.querySelector('input[name="properties[_club_count]"]').value = count;
      }

      /**
       * Handle the submit event
       */
      buildBundle() {
        // Reset the bundle sold out state
        this.setBundleSoldOut(false, window.variantStrings.addToCart);

        // Prepare the product bundle
        const heads = this.buildPairedComponents();
        const softGoods = this.getSoftGoods();
        const clubset = [...heads, ...softGoods];

        // Update the properties[_bundled_items] input
        const clubsetString = JSON.stringify({ items: clubset });
        this.querySelector('input[name="properties[_bundled_items]"]').value = clubsetString;
      }

      /**
       * Build paired components for the clubset
       * Heads, shafts, grips share a uuid
       * @returns {Object[]} - bundleLineItem(s) for heads, shafts, grips
       */
      buildPairedComponents() {
        const headLineItems = [];
        const shaftLineItems = [];
        const gripLineItems = [];
        const headcoverLineItems = [];
        const hoselLineItems = [];

        const selectedHeads = this.clubsetData.heads.filter((head) => head.dexterity === this.dexterity);
        const selectedShafts = this.clubsetData.shaft_flex[this.shaftFlex];
        const selectedGrips = this.clubsetData.grips[this.shaftFlex === 'ladies' ? 'undersize' : 'standard'];
        const selectedHeadcovers = this.clubsetData.headcovers;
        const selectedHosels = this.clubsetData.hosels.filter((hosel) => hosel.dexterity === this.dexterity);

        this.setClubCount(selectedHeads.length);

        selectedHeads.forEach((head, index) => {
          const uuid = this.generateUuid(head.variant_id);
          const shafts = selectedShafts.filter((shaft) => this.hasMappingMatch(head.mapping, shaft.mapping));
          const grips = selectedGrips.filter((grip) => this.hasMappingMatch(head.mapping, grip.mapping));
          const headcovers = selectedHeadcovers.filter((headcover) =>
            this.hasMappingMatch(head.mapping, headcover.mapping),
          );
          const hosels = selectedHosels.filter((hosel) => this.hasMappingMatch(head.mapping, hosel.mapping));

          headLineItems.push(this.bundleLineItem(head, `${index}-${uuid}`));
          shaftLineItems.push(
            ...shafts.map((shaft) => {
              return this.bundleLineItem(shaft, `${index}-${uuid}`);
            }),
          );
          gripLineItems.push(
            ...grips.map((grip) => {
              return this.bundleLineItem(grip, `${index}-${uuid}`);
            }),
          );
          headcoverLineItems.push(
            ...headcovers.map((headcover) => {
              return this.bundleLineItem(headcover, `${index}-${uuid}`);
            }),
          );
          hoselLineItems.push(
            ...hosels.map((hosel) => {
              return this.bundleLineItem(hosel, `${index}-${uuid}`);
            }),
          );
        });

        return [...headLineItems, ...shaftLineItems, ...gripLineItems, ...headcoverLineItems, ...hoselLineItems];
      }

      /**
       * One of each soft_goods item
       * @returns {Object[]} - bundleLineItem(s) for soft goods
       */
      getSoftGoods() {
        const softGoodsLineItems = this.clubsetData.soft_goods.map((softGood) => {
          return this.bundleLineItem(softGood);
        });
        return softGoodsLineItems;
      }

      /**
       * Format the properties for the line item
       * @param {string} name - The name of the property
       * @param {string|boolean|number|null} value - The value of the property
       * @returns {Object} - The formatted property
       */
      formatProperties(name, value) {
        return {
          name: name,
          value: value || null,
        };
      }

      /**
       * Prepare each bundle line item for add to cart,
       * cart transform function handles the rest
       * https://shopify.dev/docs/api/functions/reference/cart-transform
       *
       * @param {BundleComponent} component - The component to bundle
       * @param {string} uuid - To pair up head and shaft line items with each other (optional)
       * @param {boolean} isBaseProduct - Whether the line item is the base product (optional)
       * @returns {Object} - The line item object
       */
      bundleLineItem(component, uuid, isBaseProduct = false) {
        if (!uuid) uuid = this.generateUuid(component.variant_id);

        if (['shaft', 'grip'].includes(component.type) && component.inventory_quantity < this.clubCount) {
          if (component.inventory_policy === 'deny') {
            this.setBundleSoldOut(true, window.variantStrings.soldOut);
          }
        } else if (component.inventory_quantity <= 0) {
          this.setBundleSoldOut(true, window.variantStrings.soldOut);
        }

        const lineItem = {
          id: `${component.variant_id}`,
          quantity: 1,
          sku: component.sku,
          properties: [
            this.formatProperties('_bundleId', `${this.bundleId}`),
            this.formatProperties('_club_uuid', uuid),
          ],
        };

        // Drivers, Fairways, Hybrids and head
        if (
          component.type === 'head' &&
          (component.mapping.includes('Drivers') ||
            component.mapping.includes('Fairways') ||
            component.mapping.includes('Hybrids'))
        ) {
          lineItem.properties.push(
            this.formatProperties('_hidden_Adapter_Setting', 'None'),
            this.formatProperties('_hidden_Weights', component.default_weight_configuration),
          );
        }

        // Irons or Wedges and head
        if (
          component.type === 'head' &&
          (component.mapping.includes('Irons') || component.mapping.includes('Wedges'))
        ) {
          lineItem.properties.push(
            this.formatProperties('_hidden_Lie', 'Standard'),
            this.formatProperties('_hidden_Loft', 'Standard'),
            this.formatProperties('_hidden_Swingweight', 'Standard'),
          );
        }

        // Putters and head
        if (component.type === 'head' && component.mapping.includes('Putters')) {
          lineItem.properties.push(
            this.formatProperties('_hidden_Lie', 'Standard'),
            this.formatProperties('_hidden_Loft', 'Standard'),
            this.formatProperties('_hidden_Weights', component.default_weight_configuration),
          );
        }

        // Shaft length
        if (component.type === 'shaft' && !component.mapping.includes('Putters')) {
          if (this.shaftFlex === 'ladies') {
            lineItem.properties.push(this.formatProperties('_hidden_Length', 'Minus 1/2"'));
          } else {
            lineItem.properties.push(this.formatProperties('_hidden_Length', 'Standard'));
          }
        }

        if (component.type === 'shaft' && component.mapping.includes('Putters')) {
          if (this.shaftFlex === 'ladies') {
            lineItem.properties.push(this.formatProperties('_hidden_Length', '33"'));
          } else {
            lineItem.properties.push(this.formatProperties('_hidden_Length', '34"'));
          }
        }

        // Shaft tipping
        if (component.type === 'shaft') {
          lineItem.properties.push(this.formatProperties('_hidden_Tipping', 'Standard'));
        }

        // Grips
        if (component.type === 'grip') {
          lineItem.properties.push(this.formatProperties('_hidden_Wrap', 'Standard'));
        }

        // Base product
        if (isBaseProduct) {
          const shaftFlexLabel =
            this.shaftFlex === 'ladies' ? 'Ladies' : this.shaftFlex === 'senior' ? 'Senior' : 'Regular';
          const dexterityLabel = this.dexterity === 'L' ? 'Left' : 'Right';
          lineItem.properties._notes = `Dexterity@${dexterityLabel}@@Shaft Flex@${shaftFlexLabel}`;
        }
        return lineItem;
      }
    },
  );
}
